/* Admin Docker management. Hash-based tabs over /api/admin/docker/*, each tab
   fetched lazily and cached for 30s so switching back and forth is free. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { BackendCapabilities, SystemDf } from '@hub/shared';
import { api } from '../../api/client';
import { ActionButton, Alert, Card, Loading, fmtBytes, useFlash } from '../../components/ui';
import { DOCKER_BASE, TABS, errMsg, useDockerCache, type TabId } from './dockerTabs/shared';
import { ContainersTab } from './dockerTabs/Containers';
import { ImagesTab } from './dockerTabs/Images';
import { NetworksTab } from './dockerTabs/Networks';
import { VolumesTab } from './dockerTabs/Volumes';
import { StacksTab } from './dockerTabs/Stacks';

const TAB_LABELS: Record<TabId, string> = {
  containers: 'Containers',
  images: 'Images',
  networks: 'Networks',
  volumes: 'Volumes',
  stacks: 'Stacks',
};

function backendLabel(caps: BackendCapabilities): string {
  return caps.backend === 'apple' ? 'apple container' : 'docker';
}

/** A capability the backend did not report is treated as unsupported. */
function tabEnabled(tab: TabId, caps: BackendCapabilities): boolean {
  if (tab === 'stacks') return caps.compose === true;
  if (tab === 'networks') return caps.networks === true;
  if (tab === 'volumes') return caps.volumes === true;
  if (tab === 'images') return caps.images === true;
  return true;
}

export function AdminDockerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const cache = useDockerCache();
  const { show, node: headerFlash } = useFlash();

  const [caps, setCaps] = useState<BackendCapabilities | null>(null);
  const [capsError, setCapsError] = useState<string | null>(null);
  const [df, setDf] = useState<SystemDf | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [prefillImage, setPrefillImage] = useState<string | null>(null);

  const hash = location.hash.replace(/^#/, '');
  const active: TabId = useMemo(
    () => ((TABS as readonly string[]).includes(hash) ? (hash as TabId) : 'containers'),
    [hash],
  );

  useEffect(() => {
    if (location.hash !== `#${active}`) navigate(`#${active}`, { replace: true });
  }, [active, location.hash, navigate]);

  const loadDf = useCallback(() => {
    api.get<SystemDf>(`${DOCKER_BASE}/system/df`).then(setDf).catch(() => {});
  }, []);

  const bootstrap = useCallback(async () => {
    setCapsError(null);
    try {
      const c = await api.get<BackendCapabilities>(`${DOCKER_BASE}/capabilities`);
      setCaps(c);
      loadDf();
    } catch (err) {
      setCapsError(errMsg(err));
    }
  }, [loadDf]);

  useEffect(() => { void bootstrap(); }, [bootstrap]);

  const onPrefillConsumed = useCallback(() => setPrefillImage(null), []);
  const onRunImage = useCallback((reference: string) => {
    setPrefillImage(reference);
    navigate('#containers');
  }, [navigate]);

  const enabled = caps ? tabEnabled(active, caps) : false;

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Docker</h2>
          {caps && <span className="pill pill-muted">{backendLabel(caps)}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {df && (
            <span className="muted" style={{ fontSize: '0.8rem' }}>
              {df.containers.running}/{df.containers.total} containers · {df.images.total} images
              {' '}({fmtBytes(df.images.sizeBytes)}) · {fmtBytes(df.images.reclaimableBytes)} reclaimable
            </span>
          )}
          {caps?.prune?.system === true && (
            <ActionButton
              danger
              className="btn-ghost btn-sm"
              busyLabel="Pruning…"
              confirm="System prune removes all stopped containers, dangling images, unused anonymous volumes and unused networks. Continue?"
              onClick={async () => {
                try {
                  const r = await api.post<{ deleted: string[]; reclaimedBytes: number }>(
                    `${DOCKER_BASE}/prune`, { target: 'system' },
                  );
                  show(`Reclaimed ${fmtBytes(r.reclaimedBytes)}`, 'ok');
                } catch (err) {
                  show(errMsg(err), 'error');
                  throw err;
                }
                loadDf();
                setRefreshToken((n) => n + 1);
              }}
            >System prune</ActionButton>
          )}
          {headerFlash}
        </div>
      </div>

      {capsError && (
        <div className="alert-error" style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
          {capsError}
          <button
            type="button"
            className="btn-ghost btn-sm"
            style={{ marginLeft: '0.75rem' }}
            onClick={() => { void bootstrap(); }}
          >Retry</button>
        </div>
      )}

      <div style={{
        display: 'flex', gap: '0.25rem',
        borderBottom: '1px solid var(--ha-border)', marginBottom: '1.5rem',
      }}>
        {TABS.map((t) => {
          const disabled = caps ? !tabEnabled(t, caps) : false;
          return (
            <button
              key={t}
              type="button"
              className={`tab-btn${t === active ? ' tab-btn-active' : ''}`}
              disabled={disabled}
              title={disabled && caps ? `Not supported by the ${backendLabel(caps)} backend` : undefined}
              onClick={() => navigate(`#${t}`)}
            >{TAB_LABELS[t]}</button>
          );
        })}
      </div>

      <Card>
        {!caps ? (
          capsError ? <p className="muted" style={{ fontSize: '0.875rem' }}>Backend unavailable.</p> : <Loading />
        ) : !enabled ? (
          <Alert tone="warn">{TAB_LABELS[active]} are not supported by the {backendLabel(caps)} backend.</Alert>
        ) : active === 'containers' ? (
          <ContainersTab
            caps={caps}
            cache={cache}
            refreshToken={refreshToken}
            prefillImage={prefillImage}
            onPrefillConsumed={onPrefillConsumed}
          />
        ) : active === 'images' ? (
          <ImagesTab caps={caps} cache={cache} refreshToken={refreshToken} onRunImage={onRunImage} />
        ) : active === 'networks' ? (
          <NetworksTab caps={caps} cache={cache} refreshToken={refreshToken} />
        ) : active === 'volumes' ? (
          <VolumesTab caps={caps} cache={cache} refreshToken={refreshToken} />
        ) : (
          <StacksTab cache={cache} refreshToken={refreshToken} />
        )}
      </Card>
    </>
  );
}
